import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";
import { CheckCircle2 } from "lucide-react";

const readFile = (f) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(f);
});

export default function PublicForm() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/forms/${slug}`)
      .then((r) => { setForm(r.data); const init = {}; (r.data.fields || []).forEach((f) => { init[f.label] = ""; }); setValues(init); })
      .catch((e) => setError(e?.response?.data?.detail || "Form not found"));
  }, [slug]);

  const setVal = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Normalise file values
      const data = { ...values };
      await axios.post(`${BACKEND_URL}/api/public/forms/${slug}/submit`, { data });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center p-6 text-zinc-600">{error}</div>;
  if (!form) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading…</div>;

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6">
        <div className="bg-white border border-zinc-200 p-8 max-w-md text-center" style={{borderRadius: 2}}>
          <CheckCircle2 size={42} className="text-[#10B981] mx-auto mb-3"/>
          <h2 className="font-display text-2xl font-bold tracking-tight mb-2">Merci / Thanks / Köszönjük</h2>
          <p className="text-zinc-600">Your application has been submitted.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="max-w-xl mx-auto p-6 md:p-12">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">RecruitFlow AI</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mb-2">{form.title}</h1>
          {form.description && <p className="text-zinc-600">{form.description}</p>}
        </div>

        <form onSubmit={submit} className="bg-white border border-zinc-200 p-6 md:p-8 space-y-4" style={{borderRadius: 2}} data-testid="public-form">
          {(form.fields || []).map((f, i) => (
            <div key={i}>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{f.label}{f.required && <span className="text-[#E63946]"> *</span>}</label>
              {f.type === "textarea" ? (
                <textarea required={f.required} rows={4} className="mt-1 w-full border border-zinc-300 px-3 py-2.5 focus:outline-none focus:border-[#002FA7]" style={{borderRadius: 2}} value={values[f.label] || ""} onChange={(e) => setVal(f.label, e.target.value)}/>
              ) : f.type === "select" ? (
                <select required={f.required} className="mt-1 w-full border border-zinc-300 px-3 py-2.5 bg-white" style={{borderRadius: 2}} value={values[f.label] || ""} onChange={(e) => setVal(f.label, e.target.value)}>
                  <option value="">—</option>
                  {(f.options || []).map((o, j) => <option key={j} value={o}>{o}</option>)}
                </select>
              ) : f.type === "photo" || f.type === "file" ? (
                <input required={f.required} type="file" accept={f.type === "photo" ? "image/*" : ".pdf,.doc,.docx,image/*"} className="mt-1 w-full text-sm"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 3 * 1024 * 1024) { setError("File too large (max 3MB)"); return; }
                    const b64 = await readFile(file);
                    setVal(f.label, b64);
                    if (f.type !== "photo") setVal("cv_filename", file.name);
                  }}/>
              ) : (
                <input required={f.required} type={f.type === "email" ? "email" : f.type === "phone" ? "tel" : "text"} className="mt-1 w-full border border-zinc-300 px-3 py-2.5 focus:outline-none focus:border-[#002FA7]" style={{borderRadius: 2}} value={values[f.label] || ""} onChange={(e) => setVal(f.label, e.target.value)}/>
              )}
            </div>
          ))}
          <button data-testid="public-submit" disabled={loading} type="submit" className="btn-primary w-full" style={{borderRadius: 2}}>{loading ? "…" : "Submit"}</button>
          {error && <div className="text-sm text-[#E63946]">{error}</div>}
        </form>
      </div>
    </div>
  );
}
